import { api, ApiError } from "../../api";
import type { WorkOrderRow } from "../../hooks/queries";

export type LabUploadTarget = {
  fieldId: string;
  layerId: string;
  title: string;
  kind: "points" | "zones";
  fc: GeoJSON.FeatureCollection;
  workOrderId: string;
};

export function labResultsActionLabel(row: {
  kind: string;
  status: string;
  design_event_id: string | null;
  config?: { kind?: string; zone_version_id?: string } | null;
}): "Upload lab results" | "Replace results" | null {
  if (row.kind !== "sampling") return null;
  const kind = row.config?.kind;
  if (kind === "points") {
    if (pointSetIdOf(row.design_event_id) == null) return null;
  } else if (kind === "zones") {
    if (
      zoneVersionIdOf(row.design_event_id, row.config?.zone_version_id) == null
    )
      return null;
  } else return null;
  const status = row.status.toLowerCase();
  if (status.includes("cancel")) return null;
  if (status === "completed") return "Replace results";
  if (status === "requested") return "Upload lab results";
  return null;
}

export function canVisualizeOrder(row: {
  kind: string;
  status: string;
}): boolean {
  return row.kind === "sampling" && row.status.toLowerCase() === "completed";
}

export function workOrderStatusLabel(status: string): string {
  return status.toLowerCase() === "completed" ? "Complete" : status;
}

export function pointSetIdOf(designEventId: string | null): string | null {
  if (!designEventId?.startsWith("points:")) return null;
  const id = designEventId.slice("points:".length);
  return id || null;
}

export function zoneVersionIdOf(
  designEventId: string | null,
  configured: string | null | undefined,
): string | null {
  if (designEventId?.startsWith("zones:")) {
    const id = designEventId.slice("zones:".length);
    if (id) return id;
  }
  return configured || null;
}

function coord(v: unknown): number | null {
  const n =
    typeof v === "number"
      ? v
      : typeof v === "string" && v.trim() !== ""
        ? Number(v)
        : NaN;
  return Number.isFinite(n) ? n : null;
}

// Ordered points record the sample number only as `P-<digits>`. The strict
// matcher prefers `num` — the number a lab CSV carries — over `point_id`.
const ORDERED_POINT_NUM = /^P-(\d+)$/i;

function sampleNumber(num: unknown, id: unknown): unknown {
  if (num != null && String(num).trim() !== "") return num;
  const match = ORDERED_POINT_NUM.exec(String(id).trim());
  return match ? match[1] : undefined;
}

// Point features the strict matcher reads. `point_id` stays the saved id
// (`soil_results:<batch>:<property>` included); only a real sample number
// is copied onto `num`.
export function pointsFeatureCollection(
  points: unknown,
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  if (!Array.isArray(points)) return { type: "FeatureCollection", features };
  points.forEach((raw, i) => {
    if (!raw || typeof raw !== "object") return;
    const p = raw as Record<string, unknown>;
    const id = p.point_id ?? p.sample_id ?? p.id ?? `P-${i + 1}`;
    if (String(id).trim() === "") return;
    const lon = coord(p.lon ?? p.lng);
    const lat = coord(p.lat);
    const properties: Record<string, unknown> = { point_id: String(id) };
    const num = sampleNumber(p.num, id);
    if (num !== undefined) properties.num = num;
    // The matcher reads properties, not the point. A row with no coordinates
    // still has to carry its id; the upload dialog does not draw this geometry.
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: [lon ?? 0, lat ?? 0] },
      properties,
    });
  });
  return { type: "FeatureCollection", features };
}

function pointsUploadTarget(row: WorkOrderRow): LabUploadTarget {
  const setId = pointSetIdOf(row.design_event_id);
  if (setId == null)
    throw new Error("This order has no saved point set to attach results to.");
  return {
    fieldId: row.field_id,
    layerId: `target-points-${setId}`,
    title: row.field_name,
    kind: "points",
    fc: pointsFeatureCollection(row.config?.points ?? []),
    workOrderId: row.request_id,
  };
}

async function zonesUploadTarget(row: WorkOrderRow): Promise<LabUploadTarget> {
  const versionId = zoneVersionIdOf(
    row.design_event_id,
    row.config?.zone_version_id,
  );
  if (!versionId)
    throw new Error(
      "This order has no saved zone design to attach results to.",
    );
  const fc = await api.fetchZoneVersionGeojson(row.field_id, versionId);
  if (!fc?.features?.length)
    throw new Error(
      "These zones weren’t found on the server — re-save the zones, then upload the results.",
    );
  return {
    fieldId: row.field_id,
    layerId: `target-zones-${versionId}`,
    title: row.field_name,
    kind: "zones",
    fc,
    workOrderId: row.request_id,
  };
}

export async function labUploadTarget(
  row: WorkOrderRow,
): Promise<LabUploadTarget> {
  if (row.config?.kind === "points") return pointsUploadTarget(row);
  if (row.config?.kind === "zones") return zonesUploadTarget(row);
  throw new Error(
    "This order has no point or zone design to attach results to.",
  );
}

export function labResultErrorMessage(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code === "work_order_canceled")
    return "This work order was canceled, so lab results can’t be attached.";
  if (error.code === "work_order_field_mismatch")
    return "This work order belongs to a different field than these results.";
  return null;
}

function partialCounts(
  raw: unknown,
): { matched: number; expected: number } | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const nested =
    rec.partial_results && typeof rec.partial_results === "object"
      ? (rec.partial_results as Record<string, unknown>)
      : null;
  const src =
    rec.code === "partial_results" || rec.warning === "partial_results"
      ? rec
      : nested;
  if (!src) return null;
  if (typeof src.matched !== "number" || typeof src.expected !== "number")
    return null;
  return { matched: src.matched, expected: src.expected };
}

export function partialResultsNotice(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const warnings = (body as { work_order?: { warnings?: unknown } }).work_order
    ?.warnings;
  const list = Array.isArray(warnings) ? warnings : warnings ? [warnings] : [];
  for (const warning of list) {
    const counts = partialCounts(warning);
    if (counts)
      return `Partial results — matched ${counts.matched} of ${counts.expected}.`;
  }
  return null;
}
